<template>
  <div class="usermanagement">
    <h2 class="usermanagement-header fadein">Gruppenmanagement</h2>
    <div class="usermanagement-subtitle fadein d100ms">
      Erstelle, verwalte und lösche Benutzergruppen
    </div>
    <CreateGroupDialog :active="dialogOpen"></CreateGroupDialog>
    <GroupEditDialog></GroupEditDialog>
    <div class="usermanagement-card fadein d200ms">
      <TabView>
        <TabPanel header="Gruppen">
          <div>
            <DataTable
              @row-select="updateSelection"
              @row-unselect="updateSelection"
              selectionMode="single"
              :metaKeySelection="false"
              v-model:selection="selectedgroup"
              :rowsPerPageOptions="[10, 25, 50, 100, 200]"
              :rows="10"
              paginator
              :loading="loading"
              v-model:filters="filters"
              :value="groups"
              dataKey="groupid"
            >
              <template #empty> Es wurden keine Gruppen gefunden. </template>
              <template #loading
                ><ProgressSpinner
                  stroke="white"
                  stroke-width="6"
                  style="width: 3rem; height: 3rem"
                ></ProgressSpinner
              ></template>
              <Column header="Gruppenname" field="displayname">
                <template #body="slotProps">
                  <div style="display: flex; gap: 0.5rem; flex-wrap: wrap">
                    <b>{{ slotProps.data.name }}</b>
                  </div>
                </template>
              </Column>
              <Column header="Berechtigungen" field="perms" style="max-width: 20rem">
  <template #body="slotProps">
    <div style="display: flex; gap: 0.3rem; flex-wrap: wrap">
      <Tag
        v-if="slotProps.data.permissionDescriptions?.length"
        v-for="(desc, index) in slotProps.data.permissionDescriptions"
        :key="index"
        :value="desc"
      />
      <Tag
        v-if="!slotProps.data.permissionDescriptions?.length"
        severity="warning"
        value="Keine Berechtigungen"
      />
    </div>
  </template>
</Column>

              <Column header="Gruppengewichtung" field="uid">
                <template #body="slotProps">
                  <code>{{ slotProps.data.weight }}</code>
                </template>
              </Column>
              <Column header="Aktionen">
                <template #body="slotProps">
                  <div style="display: flex; gap: 0.3rem">
                    <Button severity="primary" text outlined icon="fa-regular fa-edit" @click="editDialog($event, slotProps.data.uid)"
                      :disabled="
                        !authStore.hasUserPermission('management.groups.edit') ||
                        loading ||
                        authStore.weight <= slotProps.data.weight ||
                        selectedgroup !== slotProps.data
                      " label="Bearbeiten"></Button>
                    <Button
                      severity="danger"
                      @click="deleteConfirmation($event, slotProps.data.uid)"
                      :disabled="
                        !authStore.hasUserPermission('management.groups.delete') ||
                        loading ||
                        authStore.weight <= slotProps.data.weight ||
                        selectedgroup !== slotProps.data
                      "
                      text
                      outlined
                      icon="fa-regular fa-trash"
                      label="Löschen"
                    ></Button>
                    <ConfirmPopup style="max-width: 25rem"></ConfirmPopup>
                  </div>
                </template>
              </Column>
            </DataTable>

            <Button
              style="margin-top: 1rem"
              @click="dialogOpen = true"
              :disabled="!authStore.hasUserPermission('management.groups.create')"
              outlined
              text
              label="Gruppe Erstellen"
              severity="success"
              icon="fa-solid fa-plus"
            ></Button>
          </div>
        </TabPanel>
      </TabView>
    </div>
  </div>
</template>
<script>
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import ColumnGroup from 'primevue/columngroup'
import Row from 'primevue/row'
import IconField from 'primevue/iconfield'
import InputIcon from 'primevue/inputicon'
import InputText from 'primevue/inputtext'
import TabView from 'primevue/tabview'
import Button from 'primevue/button'
import TabPanel from 'primevue/tabpanel'
import { FilterMatchMode } from 'primevue/api'
import axios from 'axios'
import { getAuth, sendPasswordResetEmail, sendEmailVerification } from 'firebase/auth'
import SelectButton from 'primevue/selectbutton'
import { config } from '../../assets/config.js'
import ConfirmPopup from 'primevue/confirmpopup'
import Tag from 'primevue/tag'
import { useAuthStore } from '@/stores/auth.js'
import CreateGroupDialog from '@/components/CreateGroupDialog.vue'
import ProgressSpinner from 'primevue/progressspinner'
import UserInformations from '../../components/UserInformations.vue'
import GroupInformations from '@/components/GroupInformations.vue'
import GroupEditDialog from '@/components/GroupEditDialog.vue'

export default {
  components: {
    GroupInformations,
    DataTable,
    Column,
    ColumnGroup,
    Row,
    IconField,
    InputIcon,
    InputText,
    SelectButton,
    TabView,
    TabPanel,
    Tag,
    Button,
    CreateGroupDialog,
    ConfirmPopup,
    ProgressSpinner,
    GroupEditDialog,
  },

  data() {
    return {
      authStore: useAuthStore(),
      auth: getAuth(),
      dialogOpen: false,
      selectedgroup: null,
      groups: [],
      loading: true,
      hidePanels: true,
      editDialogOpen: false
    }
  },

  methods: {
    updateSelection() {
      this.hidePanels =
        this.selectedgroup == null || this.selectedgroup.weight >= useAuthStore().weight
    },
    deleteConfirmation(event, userid) {
      this.$confirm.require({
        target: event.currentTarget,
        message: 'Bist du dir sicher, dass du diese Gruppe entgültig löschen möchtest?',
        icon: 'fa-solid fa-warning',
        acceptIcon: 'fa-solid fa-trash',
        acceptLabel: 'Löschen',
        acceptClass: 'p-button-danger p-button-outlined p-button-sm',
        rejectClass: 'p-button-secondary p-button-outlined p-button-sm',
        rejectLabel: 'Abbrechen',
        rejectIcon: 'fa-solid fa-xmark',

        accept: () => {
          this.startUserDeletion(userid)
        }
      })
    },

    editDialog(event, groupId) {
      this.editDialogOpen = true
    },
    async startUserDeletion(userid) {
      if (this.selectedgroup && this.selectedgroup.groupid == userid) this.selectedgroup = null
      this.loading = true
      let response = await axios.post(
        config.api_url + '/api/management/groups/delete',
        {
          uid: this.selectedgroup.groupid
        },
        {
          headers: {
            Authorization: await getAuth().currentUser.getIdToken()
          }
        }
      )

      this.fetchData()
    },
    async fetchData() {
  let response = await axios.get(config.api_url + '/api/management/groups', {
    headers: {
      Authorization: await getAuth().currentUser.getIdToken()
    }
  })

  if (response.status != 200) return

  const groupsWithDescriptions = await Promise.all(
    response.data.map(async group => {
      const permissions = group.permissions.filter(p => p.trim() !== '');
      const descriptions = await Promise.all(
        permissions.map(async perm => {
          try {
            const res = await axios.get(`${config.api_url}/api/getPermissionName/${perm}`, {
              headers: {
                Authorization: await getAuth().currentUser.getIdToken()
              }
            });
            return res.data?.[0]?.description || perm;
          } catch (err) {
            console.error('Error fetching permission:', perm, err);
            return perm;
          }
        })
      );
      return {
        ...group,
        permissionDescriptions: descriptions
      }
    })
  );

  this.groups = groupsWithDescriptions;
  this.loading = false;
},
  },

  async mounted() {
    await this.fetchData()
  }
}
</script>
<style lang="scss" scoped>
.usermanagement {
  &-header {
    margin-bottom: 0;
  }

  &-subtitle {
    color: rgb(192, 192, 192);
  }

  &-card {
    margin-top: 2rem;
    border-radius: 1rem;
    padding: 1rem;
    background-color: var(--surface-0);
  }
}

.p-progress-spinner-circle {
  stroke-dasharray: 89, 200;
  stroke-dashoffset: 0;
  stroke: #ccc;
  animation: p-progress-spinner-dash 1.5s ease-in-out infinite;
  stroke-linecap: round;
}
</style>
